import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Shield, ShieldCheck, Loader2, Send, ChevronLeft } from "lucide-react";
import { OccasionSelector } from "../components/invitations/OccasionSelector";
import { TemplateGallery } from "../components/invitations/TemplateGallery";
import { TemplateEditor, InvitationPreview } from "../components/invitations/TemplateEditor";
import { ShareModal } from "../components/invitations/ShareModal";
import { API_URL, LOGO_URL, axios } from "../constants";
import EarlyBirdStrip from "../components/EarlyBirdStrip";

const STEPS = [
  { id: "occasion", label: "Occasion" },
  { id: "template", label: "Design" },
  { id: "customize", label: "Customize" },
  { id: "share", label: "Share" },
];

const InvitationsPage = () => {
  const [step, setStep] = useState(0);
  const [occasion, setOccasion] = useState(null);
  const [customOccasion, setCustomOccasion] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customization, setCustomization] = useState({
    title: "", subtitle: "", hostName: "", dateText: "", timeText: "",
    venueText: "", rsvpText: "", photo: null, titleColor: null,
    accentColor: null, customOccasionLabel: null, fontFamily: null,
    photoSize: 80, titleFontSize: 28, contentYOffset: 0,
  });
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  const [verifyMode, setVerifyMode] = useState(false);
  const [verifyInput, setVerifyInput] = useState("");
  const [verifyType, setVerifyType] = useState("phone");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const previewRef = useRef(null);

  // --- Invitation Protection: block right-click, screenshots, copy, drag ---
  useEffect(() => {
    const handleContextMenu = (e) => {
      // Allow right-click only on input/textarea elements for UX
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      e.preventDefault();
    };

    const handleKeyDown = (e) => {
      // Block PrintScreen
      if (e.key === "PrintScreen") {
        e.preventDefault();
        navigator.clipboard?.writeText?.("").catch(() => {});
      }
      // Block Ctrl+S (save), Ctrl+Shift+I (devtools), Ctrl+U (view source), Ctrl+P (print)
      if (e.ctrlKey && ["s", "u", "p"].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
      if (e.ctrlKey && e.shiftKey && ["i", "j", "c"].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
      // Block F12
      if (e.key === "F12") {
        e.preventDefault();
      }
    };

    const handleDragStart = (e) => {
      if (e.target.tagName === "IMG") {
        e.preventDefault();
      }
    };

    const handleCopy = (e) => {
      // Prevent copying invitation content
      const selection = window.getSelection()?.toString() || "";
      if (!selection) e.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("copy", handleCopy);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("copy", handleCopy);
    };
  }, []);

  const handleOccasionSelect = (occ) => {
    setOccasion(occ);
    setSelectedTemplate(null);
    if (occ !== "other") {
      setCustomOccasion("");
      setStep(1);
    }
  };

  const handleTemplateSelect = (tpl) => {
    setSelectedTemplate(tpl);
    const labelOverride = occasion === "other" && customOccasion.trim()
      ? customOccasion.trim()
      : null;
    setCustomization({
      title: tpl.defaultTitle,
      subtitle: tpl.defaultSubtitle,
      hostName: "", dateText: "", timeText: "",
      venueText: "", rsvpText: "", photo: null,
      titleColor: tpl.textColor,
      accentColor: tpl.accentColor,
      customOccasionLabel: labelOverride,
      fontFamily: null, photoSize: 80,
      titleFontSize: 28, contentYOffset: 0,
    });
    setStep(2);
  };

  const handleVerifyBooking = async () => {
    if (!verifyInput.trim()) return;
    setVerifyLoading(true);
    setVerifyResult(null);
    try {
      const payload = verifyType === "phone"
        ? { phone: verifyInput.trim() }
        : { booking_id: verifyInput.trim() };
      const res = await axios.post(`${API_URL}/api/invitations/verify-booking`, payload);
      setVerifyResult(res.data);
      if (res.data.verified) {
        setWatermarkEnabled(false);
      }
    } catch (err) {
      setVerifyResult({ verified: false, message: "Verification failed. Please try again." });
    } finally {
      setVerifyLoading(false);
    }
  };

  const canGoNext = () => {
    if (step === 0) return !!occasion;
    if (step === 1) return !!selectedTemplate;
    return true;
  };

  const handleNextFromOccasion = () => {
    if (occasion === "other" && !customOccasion.trim()) {
      // Allow going next even without custom name — they can edit later
    }
    if (canGoNext()) setStep(1);
  };

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="invitations-page"
      style={{ WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none" }}
    >
      <EarlyBirdStrip />
      {/* Header */}
      <header className="bg-white border-b border-[#E8E8E8] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="flex items-center gap-2" data-testid="header-back-btn">
              <ChevronLeft size={20} className="text-[#475569]" />
              <img src={LOGO_URL} alt="DoozyGo" className="h-10" />
            </button>
          ) : (
            <Link to="/" className="flex items-center gap-2" data-testid="back-to-home">
              <ChevronLeft size={20} className="text-[#475569]" />
              <img src={LOGO_URL} alt="DoozyGo" className="h-10" />
            </Link>
          )}
          <div className="flex items-center gap-1 text-xs sm:text-sm font-medium text-[#475569]">
            <span className="hidden sm:inline text-[#E83E8C] font-bold">FREE</span>
            <span className="hidden sm:inline">Invitation Maker</span>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="bg-white border-b border-[#E8E8E8]">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            {STEPS.map((s, idx) => (
              <div key={s.id} className="flex items-center flex-1">
                <button
                  onClick={() => idx <= step && setStep(idx)}
                  disabled={idx > step}
                  data-testid={`step-${s.id}`}
                  className={`flex items-center gap-1.5 text-xs sm:text-sm font-medium transition-colors ${
                    idx === step ? "text-[#E83E8C]" : idx < step ? "text-[#1E1B4B] cursor-pointer" : "text-[#CBD5E1]"
                  }`}
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === step ? "bg-[#E83E8C] text-white" : idx < step ? "bg-[#1E1B4B] text-white" : "bg-[#E8E8E8] text-[#94a3b8]"
                  }`}>
                    {idx < step ? "\u2713" : idx + 1}
                  </span>
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 rounded ${idx < step ? "bg-[#1E1B4B]" : "bg-[#E8E8E8]"}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
        {step === 0 && (
          <OccasionSelector
            selected={occasion}
            onSelect={handleOccasionSelect}
            customOccasion={customOccasion}
            onCustomOccasionChange={setCustomOccasion}
          />
        )}

        {step === 1 && occasion && (
          <TemplateGallery occasion={occasion} selectedTemplate={selectedTemplate} onSelect={handleTemplateSelect} />
        )}

        {step === 2 && selectedTemplate && (
          <TemplateEditor
            template={selectedTemplate}
            customization={customization}
            onChange={setCustomization}
            watermarkEnabled={watermarkEnabled}
          />
        )}

        {step === 3 && selectedTemplate && (
          <div className="max-w-xl mx-auto space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1E1B4B] text-center">
              Your Invitation is Ready!
            </h2>

            <InvitationPreview
              template={selectedTemplate}
              customization={customization}
              watermarkEnabled={watermarkEnabled}
              previewRef={previewRef}
            />

            {/* Watermark removal */}
            <div className="bg-white rounded-2xl border border-[#E8E8E8] p-4 sm:p-5" data-testid="watermark-section">
              {watermarkEnabled ? (
                <>
                  <div className="flex items-start gap-3 mb-3">
                    <Shield size={20} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-[#1E1B4B]">Remove DoozyGo Watermark</p>
                      <p className="text-xs text-[#475569] mt-0.5">
                        Book a party with DoozyGo® to download without watermark!
                      </p>
                    </div>
                  </div>
                  {!verifyMode ? (
                    <button
                      onClick={() => setVerifyMode(true)}
                      data-testid="verify-booking-toggle"
                      className="text-sm text-[#E83E8C] font-medium hover:underline"
                    >
                      Already booked? Verify here
                    </button>
                  ) : (
                    <div className="space-y-3 mt-3 pt-3 border-t border-[#E8E8E8]">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setVerifyType("phone")}
                          className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors ${
                            verifyType === "phone" ? "bg-[#E83E8C] text-white" : "bg-[#F1F5F9] text-[#475569]"
                          }`}
                          data-testid="verify-type-phone"
                        >
                          Phone Number
                        </button>
                        <button
                          onClick={() => setVerifyType("booking_id")}
                          className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors ${
                            verifyType === "booking_id" ? "bg-[#E83E8C] text-white" : "bg-[#F1F5F9] text-[#475569]"
                          }`}
                          data-testid="verify-type-booking-id"
                        >
                          Booking ID
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <input
                          data-testid="verify-input"
                          type="text"
                          placeholder={verifyType === "phone" ? "Enter phone number" : "Enter Booking ID (e.g., DZ-260315-001)"}
                          value={verifyInput}
                          onChange={(e) => setVerifyInput(e.target.value)}
                          className="flex-1 px-3 py-2 border border-[#E8E8E8] rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#E83E8C]/20 focus:border-[#E83E8C]"
                        />
                        <button
                          onClick={handleVerifyBooking}
                          disabled={verifyLoading || !verifyInput.trim()}
                          data-testid="verify-submit-btn"
                          className="px-4 py-2 bg-[#E83E8C] text-white rounded-lg text-sm font-medium hover:bg-[#D63384] transition-colors disabled:opacity-50"
                        >
                          {verifyLoading ? <Loader2 size={16} className="animate-spin" /> : "Verify"}
                        </button>
                      </div>
                      {verifyResult && (
                        <p className={`text-xs ${verifyResult.verified ? "text-green-600" : "text-amber-600"}`} data-testid="verify-result">
                          {verifyResult.message}
                        </p>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2 text-green-600">
                  <ShieldCheck size={20} />
                  <span className="text-sm font-semibold">Watermark removed - Booking verified!</span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShareOpen(true)}
                data-testid="open-share-modal"
                className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white rounded-xl font-bold text-base hover:opacity-90 transition-opacity shadow-lg"
              >
                <Send size={18} /> Download & Share
              </button>
            </div>
          </div>
        )}

        {/* Navigation */}
        {step > 0 && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#E8E8E8]">
            <button
              onClick={() => setStep(step - 1)}
              data-testid="prev-step-btn"
              className="flex items-center gap-1 px-4 py-2.5 text-sm font-medium text-[#475569] hover:text-[#1E1B4B] transition-colors rounded-lg hover:bg-[#F1F5F9]"
            >
              <ArrowLeft size={16} /> Back
            </button>
            {step < 3 && (
              <button
                onClick={() => canGoNext() && setStep(step + 1)}
                disabled={!canGoNext()}
                data-testid="next-step-btn"
                className="flex items-center gap-1 px-6 py-2.5 text-sm font-semibold bg-[#E83E8C] text-white rounded-xl hover:bg-[#D63384] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ArrowRight size={16} />
              </button>
            )}
          </div>
        )}

        {/* Go to templates button for "Other" occasion */}
        {step === 0 && occasion === "other" && (
          <div className="flex justify-center mt-6">
            <button
              onClick={handleNextFromOccasion}
              data-testid="other-next-btn"
              className="flex items-center gap-1 px-8 py-3 text-sm font-semibold bg-[#E83E8C] text-white rounded-xl hover:bg-[#D63384] transition-colors"
            >
              Browse Designs <ArrowRight size={16} />
            </button>
          </div>
        )}
      </main>

      {/* Share Modal */}
      <ShareModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        previewRef={previewRef}
        watermarkEnabled={watermarkEnabled}
      />
    </div>
  );
};

export default InvitationsPage;
